/// <amd-module name="Artifacts/Package/services/ProvenanceLinkService" />
import {
  ExternalContributions,
  ExternalRoutes,
  ProvenanceKeys,
  ProvenanceSource,
  ReleaseRouteParameters,
  VCRouteParameters,
} from "Artifacts/Package/Constants";
import { IFeedBuildDataService } from "Artifacts/Package/services/BuildDataService";
import { Provenance } from "Artifacts/Root/web-apis/contracts/Feed";
import { Build } from "Build/Client/Contracts/Build";
import { RouteParameters as BuildRouteParameters } from "Build/Client/Linking";
import { ILinkData, ISourceProvider, RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

// PackageProvenance UI will render the text without the link if the url is null
// but will render nothing if the text is null.
export interface IProvenanceLinkData {
  url: string | null;
  text: string | null;
  isExternal: boolean;
}

export interface IProvenanceLinks {
  buildLink: IProvenanceLinkData | null;
  buildDefinitionLink: IProvenanceLinkData | null;
  repositoryLink: IProvenanceLinkData | null;
  commitLink: IProvenanceLinkData | null;
  branchLink: IProvenanceLinkData | null;
  repositoryType: string | null;
  releaseLink: IProvenanceLinkData | null;
  releaseDefinitionLink: IProvenanceLinkData | null;
  externalLink: IProvenanceLinkData | null;
}

export interface IFeedProvenanceLinkService extends IVssService {
  getLinksAsync(provenance: Provenance, publicAccessMapping: string): Promise<IProvenanceLinks>;
}

interface IBuildData {
  collectionId: string;
  projectId: string;
  buildId: number;
  buildNumber: string;
  definitionId: number | undefined;
  definitionName: string;
  repositoryName: string;
  repositoryUrl: string;
  repositoryType: string;
  repositoryId: string;
  sourceBranch: string;
  sourceVersion: string;
}

// This service assumes that provenance information for a package is in the same organization as the current page,
// because routeUrl() will set the hostPath based on the current page.
// When cross-origanization provenance collection is enabled, code here will need to be updated to ensure
// links to remote organizations are generated correctly.
export class ProvenanceLinkService extends VssService implements IFeedProvenanceLinkService {
  public async getLinksAsync(
    provenance: Provenance,
    publicAccessMapping: string,
  ): Promise<IProvenanceLinks> {
    if (provenance == null) {
      throw new Error("provenance cannot be null");
    }

    const provenanceSource = provenance.provenanceSource;

    if (Utils_String.equals(provenanceSource, ProvenanceSource.InternalBuild, true)) {
      return this.getBuildLinksAsync(provenance, publicAccessMapping);
    }
    if (Utils_String.equals(provenanceSource, ProvenanceSource.InternalRelease, true)) {
      return this.getReleaseLinksAsync(provenance, publicAccessMapping);
    }
    if (Utils_String.equals(provenanceSource, ProvenanceSource.External, true)) {
      return Promise.resolve(this.getExternalLink(provenance));
    }
    return Promise.resolve(this.NoLinks());
  }

  private async getBuildLinksAsync(
    provenance: Provenance,
    publicAccessMapping: string,
  ): Promise<IProvenanceLinks> {
    const collectionId = provenance.data[ProvenanceKeys.CollectionId];
    const projectId = provenance.data[ProvenanceKeys.ProjectId];
    const buildId = provenance.data[ProvenanceKeys.BuildId];

    if (collectionId == null || projectId == null || buildId == null) {
      return Promise.resolve(this.NoLinks());
    }

    const build: IBuildData = await this.getBuildDataAsync(
      provenance,
      collectionId,
      projectId,
      buildId,
    );
    const sourceProviderService = this.pageContext.getService<ISourceProviderService>(
      "ISourceProviderService",
    );
    const sourceProvider: ISourceProvider = sourceProviderService.getSourceProvider(
      build.repositoryType,
    );

    // All version control links are provided by ISourceProvider
    // except for TfsGit and Tfvc urls -- these we will route ourselves since
    // ISourceProvider doesn't handle cross-collection links for TfsGit
    // and does not handle cross-project links for Tfvc
    if (
      Utils_String.equals(build.repositoryType, RepositoryTypes.TfsGit, true) ||
      Utils_String.equals(build.repositoryType, RepositoryTypes.TfsVersionControl, true)
    ) {
      return Promise.resolve(
        this.getBuildLinksForInternalRepository(build, sourceProvider, publicAccessMapping),
      );
    }

    // Get external links
    const links: IProvenanceLinks = this.NoLinks();

    links.buildLink = {
      url: this.getBuildUrl(build.buildId, build.projectId, publicAccessMapping),
      text: build.buildNumber,
      isExternal: false,
    };

    if (build.definitionId) {
      links.buildDefinitionLink = {
        url: this.getDefinitionUrl(build.definitionId, build.projectId, publicAccessMapping),
        text: build.definitionName,
        isExternal: false,
      };
    }

    const linksData = await sourceProvider.fetchLinks([
      {
        branchName: build.sourceBranch,
        repositoryName: build.repositoryName || build.repositoryId,
        repositoryUrl: build.repositoryUrl,
        commitId: build.sourceVersion,
        displayUri: "", // was null
        projectName: build.projectId,
      },
    ]);
    const linkData: ILinkData | null = linksData != null ? linksData[0] : null;

    if (linkData == null) {
      return Promise.resolve(links);
    }

    links.commitLink = {
      url: linkData.commitLink || null,
      text: linkData.commitId,
      isExternal: true,
    };

    links.branchLink = {
      url: linkData.branchLink || null,
      text: sourceProvider.getBranchText(build.sourceBranch),
      isExternal: true,
    };

    links.repositoryLink = {
      url: linkData.repositoryLink || null,
      text: build.repositoryId,
      isExternal: true,
    };

    links.repositoryType = build.repositoryType;

    return Promise.resolve(links);
  }

  private getBuildLinksForInternalRepository(
    build: IBuildData,
    sourceProvider: ISourceProvider,
    publicAccessMapping: string,
  ): IProvenanceLinks {
    const links: IProvenanceLinks = this.NoLinks();

    links.buildLink = {
      url: this.getBuildUrl(build.buildId, build.projectId, publicAccessMapping),
      text: build.buildNumber,
      isExternal: false,
    };

    if (build.definitionId) {
      links.buildDefinitionLink = {
        url: this.getDefinitionUrl(build.definitionId, build.projectId, publicAccessMapping),
        text: build.definitionName,
        isExternal: false,
      };
    }

    links.repositoryType = build.repositoryType;

    if (Utils_String.equals(build.repositoryType, RepositoryTypes.TfsGit, true)) {
      links.repositoryLink = {
        url: this.getTfsGitRepositoryUrl(build.projectId, build.repositoryId, publicAccessMapping),
        text: build.repositoryName,
        isExternal: false,
      };

      links.commitLink = {
        url: this.getTfsGitCommitUrl(
          build.projectId,
          build.sourceVersion,
          build.repositoryId,
          publicAccessMapping,
        ),
        text: sourceProvider.getCommitText(build.sourceVersion),
        isExternal: false,
      };

      links.branchLink = {
        url: this.getTfsGitBranchUrl(
          build.projectId,
          build.sourceBranch,
          build.repositoryId,
          publicAccessMapping,
        ),
        text: sourceProvider.getBranchText(build.sourceBranch),
        isExternal: false,
      };
    }

    if (Utils_String.equals(build.repositoryType, RepositoryTypes.TfsVersionControl, true)) {
      links.commitLink = {
        url: this.getTfvcCommitUrl(build.projectId, build.sourceVersion, publicAccessMapping),
        text: sourceProvider.getCommitText(build.sourceVersion),
        isExternal: false,
      };

      links.repositoryLink = {
        url: this.getTfvcRepositoryUrl(build.projectId, publicAccessMapping),
        text: build.repositoryName,
        isExternal: false,
      };
    }
    return links;
  }

  private async getReleaseLinksAsync(
    provenance: Provenance,
    publicAccessMapping: string,
  ): Promise<IProvenanceLinks> {
    const links: IProvenanceLinks = this.NoLinks();
    const collectionId = provenance.data[ProvenanceKeys.CollectionId];
    const projectId = provenance.data[ProvenanceKeys.ProjectId];
    const releaseId = provenance.data[ProvenanceKeys.ReleaseId];
    const releaseName = provenance.data[ProvenanceKeys.ReleaseName];
    const releaseDefinitionId = provenance.data[ProvenanceKeys.ReleaseDefinitionId];
    const releaseDefinitionName = provenance.data[ProvenanceKeys.ReleaseDefinitionName];

    links.releaseLink = {
      url: this.getReleaseUrl(projectId, releaseId, publicAccessMapping),
      text: releaseName,
      isExternal: false,
    };
    links.releaseDefinitionLink = {
      url: await this.getReleaseDefinitionUrl(projectId, releaseDefinitionId, publicAccessMapping),
      text: releaseDefinitionName,
      isExternal: false,
    };
    return Promise.resolve(links);
  }

  private getExternalLink(provenance: Provenance): IProvenanceLinks {
    const links: IProvenanceLinks = this.NoLinks();
    links.externalLink = {
      url: provenance.data[ProvenanceKeys.ExternalLinkUrl] || null,
      text: provenance.data[ProvenanceKeys.ExternalLinkText] || null,
      isExternal: true,
    };
    return links;
  }

  private getBuildUrl(
    buildId: number,
    projectId: string,
    publicAccessMapping: string,
  ): string | null {
    if (buildId == null || Number.isNaN(buildId) || projectId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[BuildRouteParameters.BuildId] = buildId.toString();
    routeValues[BuildRouteParameters.Project] = projectId;

    try {
      const url = routeUrl(this.pageContext, ExternalRoutes.BuildResult, routeValues);
      return url;
    } catch (e) {
      return null;
    }
  }

  private getDefinitionUrl(
    definitionId: number,
    projectId: string,
    publicAccessMapping: string,
  ): string | null {
    if (definitionId == null || Number.isNaN(definitionId) || projectId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[BuildRouteParameters.DefinitionId] = definitionId.toString();
    routeValues[BuildRouteParameters.Project] = projectId;

    try {
      let url = routeUrl(this.pageContext, ExternalRoutes.DefinitionHub, routeValues);
      url += "&_a=summary";
      return url;
    } catch (e) {
      return null;
    }
  }

  private getTfsGitRepositoryUrl(
    projectId: string,
    repoId: string,
    publicAccessMapping: string,
  ): string | null {
    if (projectId == null || repoId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[VCRouteParameters.Project] = projectId;
    routeValues[VCRouteParameters.RepoName] = repoId;

    try {
      const url = routeUrl(this.pageContext, ExternalRoutes.GitFiles, routeValues);
      return url;
    } catch (e) {
      return null;
    }
  }

  private getTfsGitCommitUrl(
    projectId: string,
    commitId: string,
    repoId: string,
    publicAccessMapping: string,
  ): string | null {
    if (projectId == null || commitId == null || repoId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[VCRouteParameters.Project] = projectId;
    routeValues[VCRouteParameters.RepoName] = repoId;
    routeValues[VCRouteParameters.Parameters] = encodeURIComponent(commitId);
    try {
      const url = routeUrl(this.pageContext, ExternalRoutes.Commit, routeValues);
      return url;
    } catch (e) {
      return null;
    }
  }

  private getTfsGitBranchUrl(
    projectId: string,
    refName: string,
    repoId: string,
    publicAccessMapping: string,
  ): string | null {
    if (projectId == null || refName == null || repoId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[VCRouteParameters.Project] = projectId;
    routeValues[VCRouteParameters.RepoName] = repoId;

    const branchPrefix = "refs/heads/";
    if (refName.indexOf(branchPrefix) === 0) {
      routeValues[VCRouteParameters.Version] = "GB" + refName.substring(branchPrefix.length);
    }

    try {
      const url = routeUrl(this.pageContext, ExternalRoutes.GitFiles, routeValues);
      return url;
    } catch (e) {
      return null;
    }
  }

  private getTfvcCommitUrl(
    projectId: string,
    commitId: string,
    publicAccessMapping: string,
  ): string | null {
    if (projectId == null || commitId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[VCRouteParameters.Project] = projectId;

    let routeId = "";
    if (!Number.isNaN(parseInt(commitId, 10))) {
      routeId = ExternalRoutes.Changeset;
      routeValues[VCRouteParameters.Parameters] = encodeURIComponent(commitId);
    } else {
      routeId = ExternalRoutes.Shelveset;
      routeValues[VCRouteParameters.Shelveset] = commitId;
    }

    try {
      const url = routeUrl(this.pageContext, routeId, routeValues);
      return url;
    } catch (e) {
      return null;
    }
  }

  private getTfvcRepositoryUrl(projectId: string, publicAccessMapping: string): string | null {
    if (projectId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[VCRouteParameters.Project] = projectId;

    try {
      const url = routeUrl(this.pageContext, ExternalRoutes.TfvcFiles, routeValues);
      return url;
    } catch (e) {
      return null;
    }
  }

  private getReleaseUrl(
    projectId: string,
    releaseId: string,
    publicAccessMapping: string,
  ): string | null {
    if (projectId == null || releaseId == null) {
      return null;
    }
    const routeValues: { [key: string]: string } = {};
    routeValues[ReleaseRouteParameters.Project] = projectId;
    routeValues[ReleaseRouteParameters.ReleaseId] = releaseId;
    routeValues[ReleaseRouteParameters.Action] = ReleaseRouteParameters.ProgressActionValue;

    try {
      const url = routeUrl(this.pageContext, ExternalRoutes.Release, routeValues);
      return url;
    } catch (e) {
      return null;
    }
  }

  private getReleaseDefinitionUrl(
    projectId: string,
    releaseDefinitionId: string,
    publicAccessMapping: string,
  ): string | null {
    if (projectId == null || releaseDefinitionId == null) {
      return null;
    }
    const navigationService = this.pageContext.getService<IVssNavigationService>(
      "IVssNavigationService",
    );

    // check to see which release management contribution is enabled
    const rmContribution = navigationService.getNavigation(ExternalContributions.ReleaseExplorer);
    const rm2Contribution = navigationService.getNavigation(ExternalContributions.ReleaseExplorer2);

    if (rmContribution == null && rm2Contribution == null) {
      // unexpected, don't generate a url
      return null;
    }
    const pageData: IPageData | undefined = this.pageContext
      .getService<IVssPageService>("IVssPageService")
      .getData();
    if (pageData == null) {
      return null;
    }

    let url = pageData.hostPath
      ? `${pageData.hostPath}/${projectId}/_release?definitionId=${releaseDefinitionId}`
      : `${projectId}/_release?definitionId=${releaseDefinitionId}`;
    if (rm2Contribution != null) {
      url += "&view=mine";
    }

    return url;
  }

  private NoLinks(): IProvenanceLinks {
    return {
      buildLink: null,
      buildDefinitionLink: null,
      repositoryLink: null,
      commitLink: null,
      branchLink: null,
      repositoryType: null,
      releaseDefinitionLink: null,
      releaseLink: null,
      externalLink: null,
    };
  }

  private async getBuildDataAsync(
    provenance: Provenance,
    collectionId: string,
    projectId: string,
    buildIdString: string,
  ): Promise<IBuildData> {
    const buildId: number = parseInt(provenance.data[ProvenanceKeys.BuildId], 10);
    if (Number.isNaN(buildId)) {
      throw new Error("Build id not parseable: " + buildIdString);
    }

    let definitionId: number | undefined = parseInt(
      provenance.data[ProvenanceKeys.DefinitionId],
      10,
    );
    definitionId = Number.isNaN(definitionId) ? undefined : definitionId;

    const data = {
      collectionId,
      projectId,
      buildId,
      definitionId,
      buildNumber: provenance.data[ProvenanceKeys.BuildNumber],
      definitionName: provenance.data[ProvenanceKeys.DefinitionName],
      repositoryName: provenance.data[ProvenanceKeys.RepositoryName],
      repositoryType: provenance.data[ProvenanceKeys.RepositoryProvider],
      repositoryId: provenance.data[ProvenanceKeys.RepositoryId],
      repositoryUrl: provenance.data[ProvenanceKeys.RepositoryUri],
      sourceBranch: provenance.data[ProvenanceKeys.SourceBranch],
      sourceBranchName: provenance.data[ProvenanceKeys.SourceBranchName],
      sourceVersion: provenance.data[ProvenanceKeys.SourceVersion],
    };

    if (
      data.definitionId == null ||
      data.buildNumber == null ||
      data.definitionName == null ||
      data.repositoryName == null ||
      data.repositoryType == null ||
      data.repositoryId == null ||
      data.repositoryUrl == null ||
      data.sourceBranch == null ||
      data.sourceBranchName == null ||
      data.sourceVersion == null
    ) {
      try {
        const buildService = this.pageContext.getService<IFeedBuildDataService>(
          "IFeedBuildDataService",
        );
        const build: Build = await buildService.getBuildAsync(collectionId, projectId, buildId);

        data.buildNumber = build.buildNumber;
        if (build.definition != null) {
          data.definitionId = build.definition.id;
          data.definitionName = build.definition.name;
        }
        if (build.repository != null) {
          data.repositoryName = build.repository.name;
          data.repositoryType = build.repository.type;
          data.repositoryId = build.repository.id;
          data.repositoryUrl = build.repository.url;
        }
        data.sourceBranch = build.sourceBranch;
        data.sourceVersion = build.sourceVersion;
      } catch (err) {
        // We may fail build retrieval if the user does not have access to
        // the traget collection or if the build has been deleted.
        // Attempt to render links with existing data.
      }
    }

    return data;
  }
}

Services.add("IFeedProvenanceLinkService", { serviceFactory: ProvenanceLinkService });
